// components/three/terrain/TerrainHeightProvider.jsx
'use client'

import { createContext, useContext, useMemo } from 'react'
import { useGLTF } from '@react-three/drei'
import { buildHeightField } from '../../../lib/world/terrainHeight'

// Built once per geometry, whatever React does. The world tree suspends
// on every model it loads and a suspended render throws its useMemo
// away, so on a cold load this provider rasterised the whole floor into
// 65,536 cells seventeen times over (counted in the console) before the
// tree ever committed. The geometry object is the cache key: same floor,
// same field.
const _fields = new WeakMap()

/**
 * @typedef {import('../../../lib/world/terrainHeight').buildHeightField extends
 *   (...args: any) => infer R ? R : never} HeightField
 */

const TerrainHeightContext = createContext(/** @type {any} */ (null))

/**
 * Builds a sampleable terrain height field from terrain.glb's floor geometry and
 * provides it to descendants (the bird uses it to ground-snap its rendered Y).
 *
 * `useGLTF` caches by URL, so this shares the exact GLTF that TestMap already
 * loads — no second fetch or parse of the mesh.
 *
 * @param {Object} props
 * @param {string} [props.modelPath='/models/terrain.glb']
 * @param {React.ReactNode} props.children
 */
export function TerrainHeightProvider({ modelPath = '/models/terrain.glb', children }) {
  const { nodes } = useGLTF(modelPath)

  const field = useMemo(() => {
    const geo = nodes?.floor?.geometry
    if (!geo) return null
    if (_fields.has(geo)) return _fields.get(geo)
    let f = null
    try {
      // about a metre a cell: the ground went from 250 m across to 290 on
      // 2 Nov (the monorail's east side and the hills it tunnels into), and
      // 256 cells would have spread it to 1.14 m
      f = buildHeightField(geo, { resolution: 300 })
      if (f) {
        console.log(
          `[TerrainHeightProvider] height field ${f.cols}x${f.rows}, ` +
          `Y ${f.minY.toFixed(2)}..${f.maxY.toFixed(2)}, ${f.filledCells} cells`,
        )
      }
    } catch (err) {
      console.warn('[TerrainHeightProvider] Failed to build height field:', err)
    }
    _fields.set(geo, f)
    return f
  }, [nodes])

  return (
    <TerrainHeightContext.Provider value={field}>
      {children}
    </TerrainHeightContext.Provider>
  )
}

/**
 * Access the terrain height field. Returns null if outside a provider or the
 * field failed to build — callers must null-check and fall back to flat Y.
 */
export function useTerrainHeight() {
  return useContext(TerrainHeightContext)
}

// Preload — matches TestMap's model so the GLTF is warm before first render.
useGLTF.preload('/models/terrain.glb')
